<template>
  <div class="row h-100">
    <div class="col-12 h-100 d-flex flex-column">
      <div class="d-flex justify-content-end mb-3 pb-1">
        <b-button variant="danger" size="lg" class="px-4" v-if="canEdit" @click="deleteMilestone">
          {{ 'delete' | i18n }}
        </b-button>
        <b-button variant="primary" size="lg" class="ml-3 px-4" @click="submit" :disabled="saveDisabled" v-if="canEdit">
          {{ 'save' | i18n }}
        </b-button>
      </div>
      <div class="poppins overflow-x-hidden overflow-auto pr-1 flex1" style="position: relative">
        <Loader v-if="!milestoneLoaded" />
        <div v-else>
          <div>
            <BlockHeader name="DETAILS" class="mt-0" />
            <div class="row">
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Select
                  v-model="milestoneData.templateId"
                  :on-change="
                    (v) => {
                      templateChange();
                    }
                  "
                  :options="templateOptions"
                  form-group-id="template"
                  placeholder="template"
                  inputPlaceholder="select_template"
                  :required="true"
                  :disabled="!isNew"
                  :viewMode="!canEdit"
                />
              </div>
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Input v-model="milestoneData.name" form-group-id="Name" placeholder="Name" :required="true" :viewMode="!canEdit" />
              </div>
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Select
                  v-model="milestoneData.leader"
                  :options="usersOptions"
                  form-group-id="leader"
                  :placeholder="'Project leader' | i18n"
                  :viewMode="!canEdit"
                />
              </div>
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Input v-model="milestoneData.description" form-group-id="description" placeholder="Description" :viewMode="!canEdit" />
              </div>
              <div class="col-lg-6 col-sm-12 col-xs-12" style="display: flex; align-items: flex-start">
                <Multiselect
                  :initValues="milestoneData.remoteSupport"
                  :new-values="chosenTeamForRemoteSupport"
                  :options="milestoneMembersList"
                  form-group-id="remoteSupport"
                  placeholder="Remote Support"
                  placeholder-input="Select participants of Remote Support Team"
                  :on-change="(v) => (milestoneData.remoteSupport = v)"
                  style="width: 100%"
                  :viewMode="!canEdit"
                />
                <b-button v-b-modal.choosingRemoteSupportTeam variant="outline-secondary" size="sm" class="chose-team py-2" v-if="canEdit">
                  {{ 'choose_team' | i18n }}
                </b-button>
                <ChooseTeam id="choosingRemoteSupportTeam" :on-submit="(users) => (chosenTeamForRemoteSupport = users)" title="choose_team" />
              </div>
              <div class="col-lg-6 col-sm-12 col-xs-12" style="display: flex; align-items: flex-start">
                <Multiselect
                  :initValues="milestoneData.onSiteSupport"
                  :new-values="chosenTeamForOnSiteSupport"
                  :options="externalMilestoneParticipantsList"
                  form-group-id="onSiteSupport"
                  placeholder="On Site"
                  placeholder-input="Select participants of On-Site Team"
                  :on-change="(v) => (milestoneData.onSiteSupport = v)"
                  style="width: 100%"
                  :viewMode="!canEdit"
                />
                <b-button v-b-modal.choosingOnSiteSupportTeam variant="outline-secondary" size="sm" class="chose-team py-2" v-if="canEdit">
                  {{ 'choose_team' | i18n }}
                </b-button>
                <ChooseTeam id="choosingOnSiteSupportTeam" :on-submit="(users) => (chosenTeamForOnSiteSupport = users)" title="choose_team" />
              </div>
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <DateTime
                  v-model="milestoneData.startDate"
                  type="date"
                  :placeholder="'Start Date' | i18n"
                  form-group-id="milestone-start-date"
                  :viewMode="!canEdit"
                />
              </div>
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <DateTime
                  v-model="milestoneData.endDate"
                  type="date"
                  :placeholder="'End Date' | i18n"
                  form-group-id="milestone-end-date"
                  :viewMode="!canEdit"
                />
              </div>
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Radio
                  v-show="!isNew && isStatusChangeAbility"
                  v-model="milestoneData.status"
                  :options="milestoneStatusOptions"
                  form-group-id="Status"
                  placeholder="Status"
                  :required="true"
                  :small="true"
                  :viewMode="!canEdit"
                />
              </div>
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Checkbox
                  v-model="milestoneData.isOnSiteSupportOnly"
                  form-group-id="isOnSiteSupportOnly"
                  placeholder="Project List only (no video call, chat)"
                  :viewMode="!canEdit"
                />
              </div>
            </div>
          </div>
          <div class="mb-5" v-if="!isNew">
            <BlockHeader name="DOWNLOAD" />
            <div class="row">
              <div class="col-lg-12 col-sm-12 col-xs-12">
                <div class="download-buttons">
                  <div class="download-button" @click="download('zip')">zip</div>
                  <div
                    :class="`download-button ${!milestoneData.pdf ? 'download-button-disabled' : null}`"
                    @click="() => (milestoneData.pdf ? download('pdf') : null)"
                  >
                    pdf
                  </div>
                  <div class="download-button" @click="download('xml')">xml</div>
                  <div class="download-button" @click="download('json')">json</div>
                </div>
              </div>
            </div>
          </div>
          <div class="mb-5" v-if="!isNew">
            <BlockHeader name="GENERATE PDF" />
            <div class="row">
              <div class="col-lg-6 col-sm-6 col-xs-6">
                <Select v-model="pdfLanguage" :options="languagesOptions" form-group-id="Language" placeholder="Language" />
              </div>
              <div class="col-lg-3 col-sm-3 col-xs-3">
                <b-button variant="outline-primary" size="lg" class="px-4 download-pdf" @click="downloadPDF" :disabled="!pdfLanguage || downloading">
                  {{ downloading ? 'Generating' : 'Download' | i18n }}
                </b-button>
              </div>
            </div>
          </div>
          <div style="margin-top: 25px">
            <BlockHeader name="Report Recipients" />
            <div class="row">
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Input v-model="milestoneData.email" form-group-id="Report Recipient" placeholder="Report Recipient" :viewMode="!canEdit" />
              </div>
              <div class="col-lg-6 col-sm-12 col-xs-12">
                <Input
                  v-model="milestoneData.emailCC"
                  :tile-size="14"
                  form-group-id="Report Recipient CC"
                  placeholder="Report Recipient CC"
                  :viewMode="!canEdit"
                />
              </div>
            </div>
          </div>
          <!-- <div class="inputs-block"></div> -->
          <div v-if="selectedTemplate">
            <BlockHeader name="Template Details" />
            <Group
              v-for="group in selectedTemplate.groups"
              :key="group.id"
              :group="group"
              :set-field-value="setFieldValue"
              :fields-value="fieldsValue"
              :getFile="getFile"
              :getFileURL="getFileURL"
              groupName="Topic"
              :isSignatureDisable="isSignatureDisable"
              :viewMode="!canEdit"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapActions, mapState } from 'vuex';

import BlockHeader from '../../../common/block-header';
import Input from '../../../common/input';
import Multiselect from '../../../common/multiselect';
import Select from '../../../common/select';
import Checkbox from '../../../common/checkbox';
import Radio from '../../../common/radio';
import Group from '../../../reports/cases/components/group';
import Loader from '../../../common/loader';
import Notification from '../../../common/notification';
import DateTime from '../../../common/date-time';
import ChooseTeam from './modals/choose-team';

import { VALIDATION_RULES, PROJECT_STATUSES, ROLES, TRANSLATE_LANGUAGE, ENTRY_TYPES_DRAW } from '../../../../../../../../constants';

import { AuthStorageMixin, ProjectsMixin, ReportsMixin, DeleteRowMixin, AuthMixin } from '../../../../../mixins';
import api from '../../../../api';

import { mapMilestonePayload, isSignatureDisableFunction } from './helpers';

export default {
  title: 'Milestone',
  components: {
    Input,
    BlockHeader,
    Multiselect,
    Select,
    Group,
    Loader,
    DateTime,
    ChooseTeam,
    Radio,
    Checkbox,
  },
  mixins: [AuthStorageMixin, ProjectsMixin, ReportsMixin, DeleteRowMixin, AuthMixin],
  beforeRouteLeave(to, from, next) {
    if (this.disablePrevent) {
      return next();
    }
    const answer = window.confirm(this.$translate('You have not saved your progress Are you sure you want to leave this page'));
    if (answer) {
      next();
    } else {
      next(false);
    }
  },
  props: {
    templateId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      id: '',
      disablePrevent: true,
      milestoneStatusOptions: Object.values(PROJECT_STATUSES).map((i) => ({
        value: i,
        text: this.$translate(i),
      })),
      pdfLanguage: null,
      downloading: false,
      milestoneData: {
        name: '',
        templateId: '',
        email: '',
        emailCC: '',
        checklistNo: '',
        startDate: '',
        endDate: '',
        number: '',
        leader: null,
        description: '',
        status: PROJECT_STATUSES.ACTIVE,
        machines: [],
        customers: [],
        contacts: [],
        remoteSupport: [],
        onSiteSupport: [],
        isOnSiteSupportOnly: false,
      },
      isNew: false,
      templates: [],
      templateOptions: [],
      users: [],
      usersOptions: [],
      chosenTeamForRemoteSupport: [],
      chosenTeamForOnSiteSupport: [],
      fieldsValue: {},
      milestoneLoaded: false,
      projectExist: false,
      fieldLoaded: false,
      confirmationValues: {},
    };
  },
  computed: {
    ...mapState({
      projects: (state) => state.project.projects,
    }),
    canEdit() {
      return this.isCommonAdmin || this.isSuperUser;
    },
    isStatusChangeAbility() {
      return this.auth.id === this.milestoneData.leader || this.isCommonAdmin;
    },
    languagesOptions: function () {
      return Object.keys(TRANSLATE_LANGUAGE).map((key) => ({ text: key, value: TRANSLATE_LANGUAGE[key] }));
    },
    saveDisabled() {
      return !this.milestoneData || !this.milestoneData.templateId || !this.milestoneData.name;
    },
    milestoneMembersList() {
      const ids = this.milestoneData.onSiteSupport.map((c) => c.value);
      return this.users.filter((i) => !ids.includes(i.value));
    },
    externalMilestoneParticipantsList() {
      const ids = this.milestoneData.remoteSupport.map((c) => c.value);
      return this.users.filter((i) => !ids.includes(i.value));
    },
    contactList() {
      const ids = this.milestoneData.customers.map((c) => c.value);
      return this.contacts.filter((i) => ids.includes(i.customer.id)).map((c) => ({ value: c.id, text: `${c.firstname} ${c.surname}` }));
    },
    isSignatureDisable: function () {
      return isSignatureDisableFunction({
        selectedTemplate: this.selectedTemplate,
        confirmationValues: this.confirmationValues,
        fieldsValue: this.fieldsValue,
      });
    },
    selectedTemplate: function () {
      if (!this.milestoneData || !this.milestoneData.templateId) {
        return null;
      }

      const template = this.isNew ? this.templates.find((t) => t.id === this.milestoneData.templateId) : this.milestoneData.template;

      const groups = [];
      const groupsFields = {};

      template.fields.map((f) => {
        if (!groupsFields[f.groupId]) {
          groupsFields[f.groupId] = [];
        }
        f.required = f.rules.some((r) => r.name === VALIDATION_RULES.REQUIRED);
        groupsFields[f.groupId].push(f);
      });

      template.groups.map((g) => {
        const data = {
          name: g.name,
          id: g.id,
          isFile: g.isFile,
          file: g.file,
          isConfirmation: g.isConfirmation,
          fields: groupsFields[g.id],
        };
        groups.push(data);
      });
      return {
        name: template.name,
        id: template.id,
        groups,
      };
    },
  },
  watch: {
    milestoneData: {
      handler: function () {
        if (this.milestoneLoaded) {
          this.disablePrevent = false;
        }
      },
      deep: true,
    },
  },
  async created() {
    let milestone = {};

    this.projectId = this.$route.params.id;

    this.id = this.$route.params.milestoneId;

    try {
      const project = await api.projects.getById(this.projectId);

      if (project) {
        this.projectExist = true;
      }
    } catch (e) {
      this.projectExist = false;
    }

    try {
      milestone = await api.projectMilestones.getById(this.id);
    } catch (e) {
      console.log('No milestone');
    }

    if (milestone.data) {
      this.fieldsValue = {
        ...milestone.data.report.fields,
        ...milestone.data.fields,
      };
      this.milestoneData = milestone.data;

      this.milestoneData.remoteSupport = this.getValueText(this.milestoneData.remoteSupport, 'id', ['username', 'email']);
      this.milestoneData.onSiteSupport = this.getValueText(this.milestoneData.onSiteSupport, 'id', ['username', 'email']);

      this.milestoneData.templateId = milestone.data.template.id;
      this.isNew = !milestone.data;

      const { confirmation } = await api.projectMilestones.getConfirm(this.milestoneData.id);
      const d = {
        onSiteSupport: false,
        remoteSupport: false,
      };
      this.milestoneData.template.groups.forEach((g) => {
        if (!confirmation[g.id]) {
          this.$set(this.confirmationValues, g.id, d);
        } else {
          this.$set(this.confirmationValues, g.id, confirmation[g.id]);
        }

        this.milestoneData.template.fields.forEach((field) => {
          if (!confirmation[field.key]) {
            this.$set(this.confirmationValues, field.key, d);
          } else {
            this.$set(this.confirmationValues, field.key, confirmation[field.key]);
          }
        });
      });

      await this.loadInfo();
      return;
    }

    if (this.projects && this.projects[this.projectId]) {
      const exist = this.projects[this.projectId].milestones.find((i) => i.id === this.id);
      if (exist) {
        if (exist.milestoneData) {
          this.fieldsValue = exist.fieldsValue;
          this.milestoneData = exist.milestoneData;
          this.isNew = exist.isNew;
          await this.loadInfo();
          return;
        } else {
          this.fieldsValue = {
            ...exist.report.fields,
            ...exist.fields,
          };
          this.milestoneData = exist;

          this.milestoneData.remoteSupport = this.getValueText(this.milestoneData.remoteSupport, 'id', ['username', 'email']);
          this.milestoneData.onSiteSupport = this.getValueText(this.milestoneData.onSiteSupport, 'id', ['username', 'email']);

          this.milestoneData.templateId = exist.template.id;
          this.isNew = true;

          await this.loadInfo();
          return;
        }
      }
    }

    this.isNew = true;
    await this.loadInfo();
  },
  methods: {
    ...mapActions({ setProject: 'project/setProject' }),
    async loadInfo() {
      try {
        const templates = await this.projectsService.getTemplates();
        this.templates = templates.data;
        this.templateOptions = [...this.getValueText(templates.data)];
      } catch (e) {
        this.templates = [];
      }
      const users = await this.reportsService.getUsersLookup();
      this.usersOptions = users.data.map((item) => ({ value: item.id, text: item.username }));
      this.users = users.data.flatMap((item) => {
        if (item.role === ROLES.CAMERAUSER) return [];
        return {
          value: item.id,
          text: `${item.username} ${item.email ? `(${item.email})` : ''}`,
        };
      });
      setTimeout(() => {
        this.milestoneLoaded = true;
      }, 100);
      setTimeout(() => {
        this.fieldLoaded = true;
      }, 500);
    },
    getValueText(arr, valueName = 'id', textName = 'name') {
      return arr.map((i) => {
        let text = i[textName];
        if (Array.isArray(textName)) {
          text = `${i[textName[0]]} (${i[textName[1]]})`;
        }
        return { value: i[valueName], text };
      });
    },
    getBaseURL() {
      return this.isNew ? 'project-templates' : 'project-milestone';
    },
    download(type) {
      const auth = this.authStorageService.getAuth();
      window.open(`${axios.defaults.baseURL}/api/v1/one/${this.getBaseURL()}/${this.id}/${type}?authorization=${auth.access_token_link}`, '_blank');
    },
    async downloadPDF() {
      this.downloading = true;
      try {
        const auth = this.authStorageService.getAuth();
        fetch(
          `${axios.defaults.baseURL}/api/v1/one/project-milestone/${this.id}/translate-pdf/${this.pdfLanguage}?authorization=${auth.access_token_link}`
        )
          .then((resp) => resp.blob())
          .then((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;
            a.download = `Milesone_${this.milestoneData.name.replace(/[^0-9a-zA-Z_ -]/g, '-').replace(/ /g, '_')}_${this.pdfLanguage}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            this.downloading = false;
          })
          .catch((err) => {
            Notification({ type: 'error', message: err.message });
            this.downloading = false;
          });
      } catch (err) {
        this.downloading = false;
        Notification({ type: 'error', message: err.message });
      }
    },
    templateChange() {
      const template = this.templates.find((t) => t.id === this.milestoneData.templateId);
      this.milestoneData.name = template.name;
      this.milestoneData.description = template.description;
      this.milestoneData.email = template.email;
      this.milestoneData.emailCC = template.emailCC;
      this.milestoneData.checklistNo = template.checklistNo;
    },
    async getFile(id) {
      try {
        await axios(this.getFileURL(id));
        window.open(this.getFileURL(id), '_blank');
      } catch (error) {
        this.showNotification({ type: 'error', message: this.$translate('file_do_not_uploaded') });
      }
    },
    getFileURL(id) {
      const auth = this.authStorageService.getAuth();
      return `${axios.defaults.baseURL}/api/v1/one/${this.getBaseURL()}/${this.isNew ? this.selectedTemplate.id : this.id}/file/${id}?authorization=${
        auth.access_token_link
      }`;
    },
    async setFieldValue(field, value) {
      const isDrawField = ENTRY_TYPES_DRAW.includes(field.entryType);
      if (this.fieldLoaded) {
        this.disablePrevent = false;
      }
      if (isDrawField) {
        this.fieldsValue[field.id] = value;
        if (!value) {
          this.fieldsValue[field.key] = value;
        }
      } else {
        this.fieldsValue[field.key] = value;
      }
    },
    deleteMilestone() {
      this.deleteSelectedRows(
        {},
        async () => {
          this.deleteMilestoneConfirmed();
        },
        false
      );
    },
    async deleteMilestoneConfirmed() {
      try {
        if (this.projectExist) {
          await api.projectMilestones.delete(this.id);
        } else {
          const project = { ...this.projects[this.projectId] };
          project.milestones = project.milestones.filter((i) => i.id !== this.id);
          this.setProject({ id: this.projectId, project });
        }
        Notification({ type: 'success', message: this.$translate('success') });
        this.disablePrevent = true;
        this.$router.go(-1);
      } catch (err) {
        Notification({ type: 'error', message: err.message });
      }
    },
    async submit() {
      try {
        const milestoneValue = this.milestoneData;
        const fieldsValue = this.fieldsValue;
        const template = milestoneValue.template || this.templates.find((t) => t.id === milestoneValue.templateId);

        const payloadBase = {
          ...milestoneValue,
          project: this.projectId,
          fields: template.fields.map((f) => {
            const isDrawField = ENTRY_TYPES_DRAW.includes(f.entryType);
            return {
              ...f,
              value: isDrawField ? fieldsValue[f.id] : fieldsValue[f.key],
              isAttachFileToEmail: Object.hasOwn(this.fieldsValue || {}, `${f.key}_attachToEmail`)
                ? fieldsValue[`${f.key}_attachToEmail`]
                : f.isAttachFileToEmail,
            };
          }),
          remoteSupport: milestoneValue.remoteSupport.map((c) => c.value),
          onSiteSupport: milestoneValue.onSiteSupport.map((c) => c.value),
        };

        if (this.projectExist) {
          if (this.isNew) {
            const payload = mapMilestonePayload(payloadBase);
            await api.projectMilestones.create(payload);
          } else {
            const info = mapMilestonePayload(payloadBase, true, false);
            const fields = mapMilestonePayload(payloadBase, false, true);
            await api.projectMilestones.updateInfo(info, this.id);
            if (this.milestoneData.status !== PROJECT_STATUSES.FINISHED) {
              await api.projectMilestones.updateFields(fields, this.id);
            }
          }
          Notification({ type: 'success', message: this.$translate('success') });
        } else {
          const project = { ...this.projects[this.projectId] };
          const exist = project.milestones.find((i) => i.id === this.id);
          if (exist) {
            project.milestones = project.milestones.map((i) => {
              if (i.id === this.id) {
                return {
                  milestoneData: this.milestoneData,
                  fieldsValue: this.fieldsValue,
                  isNew: true,
                  id: this.id,
                };
              } else {
                return i;
              }
            });
          } else {
            project.milestones.push({ milestoneData: this.milestoneData, fieldsValue: this.fieldsValue, isNew: true, id: this.id });
          }

          if (!this.disablePrevent) {
            project.changed = true;
          }

          this.setProject({ id: this.projectId, project });
        }
        this.disablePrevent = true;
        this.$router.go(-1);
      } catch (err) {
        console.log('Error:', err);
        Notification({ type: 'error', message: err.message });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.main-content {
  overflow-y: visible;
  max-height: 100%;
  height: 100%;
}
.inputs-block {
  padding-left: 20px;
  padding-right: 20px;
}
::v-deep .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
::v-deep .active-status {
  background-color: rgb(37, 154, 43);
}
::v-deep .inprogress-status {
  background-color: rgb(239, 255, 15);
}
::v-deep .finished-status {
  background-color: rgb(90, 98, 107);
}

.statuses-block {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
  width: 100%;
}

.status-block {
  display: flex;
  margin-left: 20px;
  align-content: center;
  justify-content: center;
  align-items: center;
}

.status-name {
  margin-left: 10px;
}

::v-deep .details-action {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: rgb(34, 129, 254);
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 28px;
  margin: auto;
}

::v-deep .download-buttons {
  display: flex;
}
::v-deep .download-button {
  background: rgb(237, 248, 254);
  border: 2px solid rgb(175, 216, 255);
  border-radius: 4px;
  padding: 2px 13px;
  margin: 0px 5px;
  color: rgb(49, 141, 255);
  cursor: pointer;
}

::v-deep .zip-link {
  padding: 4.5px 13px;
  cursor: pointer;
  display: inline-block;
  outline: none;
  font-family: 'Poppins', sans-serif;
  font-style: normal;
  font-weight: 500;
  font-size: 0.875rem;
  line-height: 1.5;

  text-decoration: none;
  margin-right: 25px;

  background: #edf8fe;
  border: 2px solid #afd8ff;
  border-radius: 4px;
  color: #318dff;
}

.add-action {
  border-radius: 50% !important;
  font-size: 24px !important;
  padding: 0px !important;
  width: 40px !important;
  height: 40px !important;
  display: flex;
  justify-content: center;
  align-items: center;
}

.add-action-error {
  background: #dc3545;
}

.header-wrapper {
  position: relative;
}

.action-header-box {
  position: absolute;
  top: 0px;
  right: 0px;
}

.entity-block {
  padding: 10px;
  box-shadow: 0px 0px 35px -20px rgba(0, 0, 0, 0.75);
  margin: 5px;
  position: relative;
}

.contacts-wrapper {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin-top: 10px;
}

.icon-color {
  color: #2260ff;
}

.entity-block-part {
  box-shadow: 0px 0px 35px -15px rgba(0, 0, 0, 0.75) !important;
}

.close-icon {
  position: absolute;
  right: -8px;
  top: -8px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #007bff;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  cursor: pointer;
}

.download-buttons {
  display: flex;
}
.download-button {
  background: rgb(237, 248, 254);
  border: 2px solid rgb(175, 216, 255);
  border-radius: 4px;
  padding: 2px 13px;
  margin: 0px 5px;
  color: rgb(49, 141, 255);
  cursor: pointer;
}
.download-button-disabled {
  opacity: 0.5;
}
.chose-team {
  white-space: nowrap;
  margin-left: 10px;
  margin-top: 28px;
  line-height: 1.7rem;
}
.overflow-x-hidden {
  overflow-x: hidden !important;
}
::v-deep .btn-lg {
  font-size: 16px;
  line-height: 24px;
}

.download-pdf {
  margin-top: 28px;
  height: 46px;
}
</style>
